/**
 * FE-09: System Health page — component health, queue stats, cron tasks.
 * Auto-refreshes every 30 seconds.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchCronStatus,
  fetchQueueStats,
  fetchSystemHealth,
} from "../../api/admin-system";
import type {
  CronResult,
  HealthResult,
  QueueStats,
} from "../../api/admin-system";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { SystemAlertsPanel } from "./SystemAlertsPanel";

const POLL_INTERVAL_MS = 30_000;
type ComponentName = "api" | "neon" | "kv" | "r2";
const LABELS: Record<ComponentName, string> = {
  api: "health.api",
  neon: "health.neon",
  kv: "health.kv",
  r2: "health.r2",
};

// ─── Main ───

export function HealthPage(): React.JSX.Element {
  const { health, queues, cron, error, loading, refresh } = useSystemData();
  return (
    <div>
      <PageHeader
        title={t("health.title")}
        description={t("health.description")}
        actions={
          <button type="button" onClick={refresh} style={shared.refreshBtn}>
            {t("common.refresh")}
          </button>
        }
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !health && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {health && <ComponentCards health={health} />}
      <SystemAlertsPanel />
      {queues && <QueueSection queues={queues} />}
      {cron && <CronSection cron={cron} />}
    </div>
  );
}

// ─── Component Health Cards ───

function ComponentCards({
  health,
}: { health: HealthResult }): React.JSX.Element {
  const names: ComponentName[] = ["api", "neon", "kv", "r2"];
  return (
    <section style={shared.section}>
      <h2 style={shared.sectionTitle}>{t("health.components")}</h2>
      <div style={shared.cardGrid}>
        {names.map((name) => {
          const c = health.components[name];
          return (
            <div key={name} style={shared.card}>
              <div style={s.cardHeader}>
                <span style={shared.cardLabel}>{t(LABELS[name])}</span>
                <ColorBadge label={c.status} color={HEALTH_COLORS[c.status]} />
              </div>
              {c.latency_ms !== undefined && (
                <p style={s.meta}>{c.latency_ms} ms</p>
              )}
              {c.error && <p style={s.cardError}>{c.error}</p>}
            </div>
          );
        })}
      </div>
      <p style={s.timestamp}>
        {t("health.checked_at")}{" "}
        {new Date(health.checked_at).toLocaleTimeString()}
      </p>
    </section>
  );
}

// ─── Queue Stats ───

function QueueSection({ queues }: { queues: QueueStats }): React.JSX.Element {
  const j = queues.recent_job_stats;
  return (
    <section style={shared.section}>
      <h2 style={shared.sectionTitle}>{t("health.queue_stats")}</h2>
      <div style={shared.cardGrid}>
        <StatCard
          label={t("health.poison_messages")}
          value={queues.poison_message_count}
        />
        <StatCard label={t("health.pending_jobs")} value={j.pending} />
        <StatCard label={t("health.running_jobs")} value={j.running} />
        <StatCard label={t("health.failed_24h")} value={j.failed_24h} warn />
      </div>
    </section>
  );
}

// ─── Cron Table ───

function CronSection({ cron }: { cron: CronResult }): React.JSX.Element {
  return (
    <section style={shared.section}>
      <h2 style={shared.sectionTitle}>{t("health.cron_tasks")}</h2>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("health.col_name")}</th>
            <th style={s.th}>{t("health.col_status")}</th>
            <th style={s.th}>{t("health.col_detail")}</th>
          </tr>
        </thead>
        <tbody>
          {cron.tasks.map((t) => (
            <tr key={t.name}>
              <td style={s.td}>{t.name}</td>
              <td style={s.td}>
                <ColorBadge label={t.status} color={CRON_COLORS[t.status]} />
              </td>
              <td style={s.td}>{t.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ─── Sub-components ───

const HEALTH_COLORS: Record<string, string> = {
  healthy: "#16a34a",
  degraded: "#ca8a04",
  down: "#dc2626",
};
const CRON_COLORS: Record<string, string> = {
  ok: "#16a34a",
  warning: "#ca8a04",
  unknown: "#6b7280",
};

function ColorBadge({
  label,
  color,
}: { label: string; color: string }): React.JSX.Element {
  return <span style={{ ...shared.badge, background: color }}>{label}</span>;
}

function StatCard({
  label,
  value,
  warn,
}: { label: string; value: number; warn?: boolean }): React.JSX.Element {
  const isAlert = warn && value > 0;
  return (
    <div style={shared.card}>
      <span style={shared.cardLabel}>{label}</span>
      <span
        style={{
          ...shared.bigNumber,
          marginTop: 8,
          color: isAlert ? "#dc2626" : "var(--color-text)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Data Hook ───

type SystemData = {
  health: HealthResult | null;
  queues: QueueStats | null;
  cron: CronResult | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
};

function useSystemData(): SystemData {
  const [health, setHealth] = useState<HealthResult | null>(null);
  const [queues, setQueues] = useState<QueueStats | null>(null);
  const [cron, setCron] = useState<CronResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [h, q, cr] = await Promise.all([
        fetchSystemHealth(),
        fetchQueueStats(),
        fetchCronStatus(),
      ]);
      setHealth(h);
      setQueues(q);
      setCron(cr);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { health, queues, cron, error, loading, refresh: load };
}

// ─── Page-specific Styles ───

const s: Record<string, React.CSSProperties> = {
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  meta: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontFamily: "var(--font-mono)",
    marginTop: 4,
  },
  cardError: {
    fontSize: 12,
    color: "var(--color-danger)",
    marginTop: 4,
    wordBreak: "break-all" as const,
  },
  timestamp: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginTop: 8,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: 14,
  },
  th: {
    textAlign: "left" as const,
    padding: "8px 12px",
    borderBottom: "2px solid var(--color-border)",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
  },
  td: {
    padding: "8px 12px",
    borderBottom: "1px solid var(--color-border)",
  },
};
